import React, { useCallback, useEffect, useState } from "react";
import Breadcrumb from "component/common/breadcrumb";
import TableListSkeleton from "component/common/TableListSkeleton";
import Pagination from "component/common/Pagination";
import Select from "react-select";
import moment from "moment";
import strings from "translate/language";
import WebReportService from "reduxStore/service/webReportService";
import { errorToast, infoToast, successToast } from "utils/httpClient";
import { seo } from "utils/utils";
import { getStatusBadgeIconClass } from "utils/statusBadgeIcons";
import "../Visitor/VisitorListing.css";
import "./MdocAuditLog.css";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_RETRY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  mobile: "",
  birth_date: "",
};

const formatJson = (value) => {
  if (value == null) return "N/A";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    errorToast("Unable to copy to clipboard.");
  }
};

const downloadPayload = (text, filename) => {
  try {
    const blob = new Blob([text], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  } catch {
    errorToast("Unable to download payload.");
  }
};

const resolveVisitorName = (row) => {
  if (row?.visitor_name) return row.visitor_name;
  const payload = row?.request_payload;
  if (!payload || typeof payload !== "object") return "N/A";
  const first = payload.FirstName ? String(payload.FirstName).trim() : "";
  const last = payload.LastName ? String(payload.LastName).trim() : "";
  const full = [first, last].filter(Boolean).join(" ").trim();
  return full || "N/A";
};

const renderSyncStatusBadge = (status) => {
  const isSuccess = status === "success";
  const badgeClass = isSuccess
    ? "visitor-status-badge--booking"
    : "visitor-status-badge--cancelled";
  return (
    <span className={`visitor-status-badge ${badgeClass}`}>
      <span className="visitor-status-badge__icon" aria-hidden="true">
        <i className={getStatusBadgeIconClass(badgeClass)} />
      </span>
      <span className="visitor-status-badge__label">
        {isSuccess ? strings.success : strings.failure}
      </span>
    </span>
  );
};

const getHttpStatusBadgeClass = (code) => {
  const numeric = Number(code);
  if (!Number.isFinite(numeric)) {
    return "visitor-status-badge--default";
  }
  if (numeric >= 200 && numeric < 300) {
    return "visitor-status-badge--booking";
  }
  if (numeric >= 400 && numeric < 500) {
    return "visitor-status-badge--followup";
  }
  if (numeric >= 500) {
    return "visitor-status-badge--cancelled";
  }
  return "visitor-status-badge--default";
};

const renderHttpStatusBadge = (code) => {
  const label =
    code === null || code === undefined || code === ""
      ? "N/A"
      : String(code);
  const badgeClass = getHttpStatusBadgeClass(code);
  return (
    <span className={`visitor-status-badge ${badgeClass}`}>
      <span className="visitor-status-badge__icon" aria-hidden="true">
        <i className={getStatusBadgeIconClass(badgeClass)} />
      </span>
      <span className="visitor-status-badge__label">{label}</span>
    </span>
  );
};

const MdocAuditLog = ({ isAuth = false }) => {
  const today = moment().format("YYYY-MM-DD");
  const [form, setForm] = useState({
    start_date: today,
    end_date: today,
    visitor_name: "",
    phone_number: "",
    mdoc_property_id: "",
  });
  const [sortOrder, setSortOrder] = useState("oldest");
  const [mdocPropertyOptions, setMdocPropertyOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [retryTarget, setRetryTarget] = useState(null);
  const [retryForm, setRetryForm] = useState(EMPTY_RETRY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [retryingId, setRetryingId] = useState("");

  const loadLogs = useCallback(
    async (page = 0, filters = form, limit = itemsPerPage, sort = sortOrder) => {
      setLoading(true);
      try {
        const res = await WebReportService.getMdocAuditLogs({
          ...filters,
          sort_order: sort,
          limit,
          offset: page * limit,
        });
        if (res?.data?.status === 200) {
          setRows(Array.isArray(res.data.data) ? res.data.data : []);
          setTotal(res.data.total ?? 0);
        } else {
          setRows([]);
          setTotal(0);
          errorToast(res?.data?.message || strings.initialError);
        }
      } catch (e) {
        setRows([]);
        setTotal(0);
        const message =
          (e.response && e.response.data && e.response.data.message) ||
          strings.initialError;
        errorToast(message);
      } finally {
        setLoading(false);
      }
    },
    [form, itemsPerPage, sortOrder],
  );

  useEffect(() => {
    seo({
      title: strings.mdocAuditLog,
      metaDescription: strings.mdocAuditLog,
      canonicalLink: window.location.href,
    });
    WebReportService.getMdocProperties()
      .then((res) => {
        if (res?.data?.status === 200) {
          const list = Array.isArray(res.data.data) ? res.data.data : [];
          setMdocPropertyOptions(
            list.map((item) => ({
              value: item.property_id || "",
              label:
                item.mdoc_property_name ||
                item.property_title ||
                item.property_id ||
                strings.mdocProperty,
            })),
          );
        }
      })
      .catch(() => setMdocPropertyOptions([]));
    loadLogs(0, form, itemsPerPage, "oldest");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputField = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const filterDetail = () => {
    setCurrentPage(0);
    setExpanded({});
    loadLogs(0, form, itemsPerPage, sortOrder);
  };

  const filterReset = () => {
    const reset = {
      start_date: today,
      end_date: today,
      visitor_name: "",
      phone_number: "",
      mdoc_property_id: "",
    };
    setForm(reset);
    setSortOrder("oldest");
    setCurrentPage(0);
    setExpanded({});
    loadLogs(0, reset, itemsPerPage, "oldest");
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setExpanded({});
    loadLogs(newPage, form, itemsPerPage, sortOrder);
  };

  const handlePageSizeChange = (e) => {
    const nextSize = Number(e.target.value);
    setItemsPerPage(nextSize);
    setCurrentPage(0);
    setExpanded({});
    loadLogs(0, form, nextSize, sortOrder);
  };

  const handleSortChange = (e) => {
    const nextSort = e.target.value;
    setSortOrder(nextSort);
    setCurrentPage(0);
    setExpanded({});
    loadLogs(0, form, itemsPerPage, nextSort);
  };

  const toggleExpanded = (id, field) => {
    const key = `${id}-${field}`;
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const closeRetryModal = () => {
    if (retryingId) return;
    setRetryTarget(null);
    setRetryForm(EMPTY_RETRY_FORM);
    setFieldErrors({});
  };

  const openRetryModal = (row) => {
    const edit = row?.visitor_edit || {};
    setRetryForm({
      first_name: edit.first_name || "",
      last_name: edit.last_name || "",
      email: edit.email || "",
      mobile: edit.mobile || row?.phone_number || "",
      birth_date: edit.birth_date || "",
    });
    setFieldErrors({});
    setRetryTarget(row);
  };

  const handleRetryFieldChange = (e) => {
    const { name, value } = e.target;
    setRetryForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validateRetryForm = () => {
    const nextErrors = {};
    if (!String(retryForm.first_name || "").trim()) {
      nextErrors.first_name = strings.mdocFirstNameRequired;
    }
    const mobileDigits = String(retryForm.mobile || "").replace(/\D/g, "");
    if (!/^\d{10}$/.test(mobileDigits)) {
      nextErrors.mobile = strings.mdocMobileRequired;
    }
    if (!EMAIL_PATTERN.test(String(retryForm.email || "").trim())) {
      nextErrors.email = strings.mdocEmailRequiredHint;
    }
    if (retryForm.birth_date) {
      const dob = moment(retryForm.birth_date, "YYYY-MM-DD", true);
      const ageYears = dob.isValid() ? moment().diff(dob, "years") : NaN;
      if (!dob.isValid() || ageYears < 15 || ageYears > 100) {
        nextErrors.birth_date = strings.mdocDobInvalid;
      }
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const confirmRetry = async () => {
    if (!retryTarget?._id) return;
    if (!validateRetryForm()) return;
    setRetryingId(retryTarget._id);
    try {
      const res = await WebReportService.retryMdocPush({
        log_id: retryTarget._id,
        visitor: {
          first_name: String(retryForm.first_name || "").trim(),
          last_name: String(retryForm.last_name || "").trim(),
          email: String(retryForm.email || "").trim(),
          mobile: String(retryForm.mobile || "").replace(/\D/g, ""),
          birth_date: String(retryForm.birth_date || "").trim(),
        },
      });
      const feedback = res?.data?.data;
      if (res?.data?.status === 200) {
        if (feedback?.code === "success") {
          successToast(strings.mdocRetrySuccess, {
            title: strings.mdocSyncSuccessTitle,
          });
        } else if (feedback?.code === "dry_run") {
          infoToast(strings.mdocSyncDryRunMessage, {
            title: strings.mdocSyncDryRunTitle,
          });
        } else if (feedback?.code === "already_pushed") {
          infoToast(
            feedback.message ||
              res.data.message ||
              strings.mdocRetryAlreadySynced,
          );
        } else {
          errorToast(
            feedback?.message ||
              res.data.message ||
              strings.mdocRetryFailed,
            { title: strings.mdocSyncFailedTitle },
          );
        }
        setRetryTarget(null);
        setRetryForm(EMPTY_RETRY_FORM);
        setFieldErrors({});
        loadLogs(currentPage, form, itemsPerPage, sortOrder);
      } else {
        if (res?.data?.field_errors) {
          setFieldErrors(res.data.field_errors);
        }
        errorToast(res?.data?.message || strings.mdocRetryFailed);
      }
    } catch (e) {
      const data = e.response && e.response.data;
      if (data?.field_errors) {
        setFieldErrors(data.field_errors);
      }
      const message = data?.message || strings.mdocRetryFailed;
      errorToast(message);
    } finally {
      setRetryingId("");
    }
  };

  const isRowPayloadOpen = (rowId) =>
    Boolean(expanded[`${rowId}-request_payload`]) ||
    Boolean(expanded[`${rowId}-response_payload`]);

  const renderPayloadActions = (row, field) => {
    const payload = row[field];
    const key = `${row._id}-${field}`;
    const isOpen = Boolean(expanded[key]);
    const formatted = formatJson(payload);
    const hasPayload = payload != null;

    if (!hasPayload) {
      return <span className="mdoc-audit-payload__na">N/A</span>;
    }

    const downloadName = `mdoc-${field}-${row._id || "payload"}.json`;

    return (
      <div className="mdoc-audit-payload__actions">
        <button
          type="button"
          className={`mdoc-audit-payload__action${
            isOpen ? " mdoc-audit-payload__action--active" : ""
          }`}
          onClick={() => toggleExpanded(row._id, field)}
          title={isOpen ? strings.collapse : strings.expand}
          aria-label={isOpen ? strings.collapse : strings.expand}
        >
          <i
            className={`fa ${isOpen ? "fa-compress" : "fa-expand"}`}
            aria-hidden="true"
          />
          <span>{isOpen ? strings.collapse : strings.expand}</span>
        </button>
        <button
          type="button"
          className="mdoc-audit-payload__action"
          onClick={() => copyToClipboard(formatted)}
          title={strings.copy}
          aria-label={strings.copy}
        >
          <i className="fa fa-copy" aria-hidden="true" />
          <span>{strings.copy}</span>
        </button>
        <button
          type="button"
          className="mdoc-audit-payload__action"
          onClick={() => downloadPayload(formatted, downloadName)}
          title="Download"
          aria-label="Download"
        >
          <i className="fa fa-download" aria-hidden="true" />
        </button>
      </div>
    );
  };

  const renderExpandedPanels = (row) => {
    const requestOpen = Boolean(expanded[`${row._id}-request_payload`]);
    const responseOpen = Boolean(expanded[`${row._id}-response_payload`]);
    if (!requestOpen && !responseOpen) return null;

    const visitorLabel = resolveVisitorName(row);

    return (
      <tr className="mdoc-audit-log__expand-row">
        <td colSpan={9}>
          <div className="mdoc-audit-log__payload-grid">
            {requestOpen && row.request_payload != null && (
              <div className="mdoc-audit-payload__panel mdoc-audit-payload__panel--request">
                <div className="mdoc-audit-payload__panel-header">
                  <span>{strings.requestPayload}</span>
                  <span>{visitorLabel}</span>
                </div>
                <pre className="mdoc-audit-payload__panel-body">
                  {formatJson(row.request_payload)}
                </pre>
              </div>
            )}
            {responseOpen && row.response_payload != null && (
              <div className="mdoc-audit-payload__panel mdoc-audit-payload__panel--response">
                <div className="mdoc-audit-payload__panel-header">
                  <span>{strings.responsePayload}</span>
                  <span>{visitorLabel}</span>
                </div>
                <pre className="mdoc-audit-payload__panel-body">
                  {formatJson(row.response_payload)}
                </pre>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="page-body justo-report-page justo-mdoc-page">
      {isAuth && (
        <Breadcrumb
          title={strings.mdocAuditLog}
          parentListing={null}
          titleactive={strings.mdocAuditLog}
        />
      )}
      <div className="container-fluid justo-report justo-mdoc">
        <div className="justo-report__card justo-mdoc__card">
          <div className="justo-report__top">
            <div className="justo-report__title-block">
              <h1 className="justo-report__title">MDoc Audit Report</h1>
              <p className="justo-report__subtitle">
                Review MDoc sync attempts, HTTP status, and request/response
                payloads.
              </p>
            </div>
          </div>

          <form
            className="justo-report__filters"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="justo-report__filters-grid">
              <label className="justo-report__field">
                <span className="justo-report__label">{strings.startDate}</span>
                <input
                  className="form-control justo-report__input"
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleInputField}
                />
              </label>
              <label className="justo-report__field">
                <span className="justo-report__label">{strings.endDate}</span>
                <input
                  className="form-control justo-report__input"
                  type="date"
                  name="end_date"
                  value={form.end_date}
                  onChange={handleInputField}
                  min={form.start_date}
                />
              </label>
              <label className="justo-report__field">
                <span className="justo-report__label">
                  {strings.searchVisitorName}
                </span>
                <input
                  className="form-control justo-report__input"
                  type="text"
                  autoComplete="off"
                  name="visitor_name"
                  value={form.visitor_name}
                  onChange={handleInputField}
                  placeholder={strings.searchVisitorName}
                />
              </label>
              <label className="justo-report__field">
                <span className="justo-report__label">
                  {strings.searchByMobileNumber}
                </span>
                <input
                  className="form-control justo-report__input"
                  type="text"
                  autoComplete="off"
                  name="phone_number"
                  value={form.phone_number}
                  onChange={handleInputField}
                  placeholder={strings.searchMobileNumber}
                  maxLength="10"
                />
              </label>
              <div className="justo-report__field">
                <span className="justo-report__label">
                  {strings.mdocProperty}
                </span>
                <Select
                  className="justo-mdoc-select"
                  classNamePrefix="justo-mdoc-select"
                  isSearchable
                  isClearable
                  options={mdocPropertyOptions}
                  value={
                    form.mdoc_property_id
                      ? mdocPropertyOptions.find(
                          (opt) => opt.value === form.mdoc_property_id,
                        ) || null
                      : null
                  }
                  onChange={(selected) => {
                    setForm((prev) => ({
                      ...prev,
                      mdoc_property_id: selected ? selected.value : "",
                    }));
                  }}
                  placeholder={strings.mdocProperty}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </div>
            <div className="justo-report__filter-footer">
              <div className="justo-report__filter-actions">
                <button
                  className="justo-report__btn justo-report__btn--solid"
                  type="button"
                  onClick={filterDetail}
                >
                  {strings.search || strings.submit}
                </button>
                <button
                  className="justo-report__btn justo-report__btn--ghost"
                  type="button"
                  onClick={filterReset}
                >
                  {strings.reset}
                </button>
              </div>
            </div>
          </form>

          {total > 0 && (
            <div className="justo-report__toolbar mdoc-audit-log__toolbar">
              <div className="mdoc-audit-log__toolbar-sort">
                <label htmlFor="mdoc-audit-sort-order">
                  {strings.sortOrder}
                </label>
                <select
                  id="mdoc-audit-sort-order"
                  className="form-control justo-report__input"
                  value={sortOrder}
                  onChange={handleSortChange}
                >
                  <option value="oldest">{strings.sortOldestFirst}</option>
                  <option value="latest">{strings.sortLatestFirst}</option>
                </select>
              </div>
            </div>
          )}

          <div className="justo-report__table-wrap">
            {loading ? (
              <div className="justo-mdoc__scroll">
                <TableListSkeleton rows={10} columns={9} />
              </div>
            ) : rows.length === 0 ? (
              <div className="justo-mdoc__empty">{strings.noDataFound}</div>
            ) : (
              <div className="justo-mdoc__scroll">
                <table className="table justo-mdoc__table mdoc-audit-log__table">
                  <thead>
                    <tr>
                      <th>{strings.timestamp}</th>
                      <th>{strings.visitorName}</th>
                      <th>{strings.mobile}</th>
                      <th>{strings.capturedBy}</th>
                      <th className="mdoc-audit-log__status-col">
                        {strings.syncStatus}
                      </th>
                      <th>{strings.requestPayload}</th>
                      <th>{strings.responsePayload}</th>
                      <th className="mdoc-audit-log__http-col">
                        {strings.httpStatusCode}
                      </th>
                      <th className="mdoc-audit-log__action-col">
                        {strings.action}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <React.Fragment key={row._id}>
                        <tr>
                          <td className="mdoc-audit-log__timestamp">
                            {row.timestamp
                              ? moment(row.timestamp).format(
                                  "DD/MM/YYYY HH:mm:ss",
                                )
                              : "N/A"}
                          </td>
                          <td>{resolveVisitorName(row)}</td>
                          <td>{row.phone_number || "N/A"}</td>
                          <td>{row.captured_by_name || "N/A"}</td>
                          <td className="mdoc-audit-log__status-col">
                            {renderSyncStatusBadge(row.sync_status)}
                            {row.error_message ? (
                              <span
                                className="mdoc-audit-log__error"
                                title={row.error_message}
                              >
                                {row.error_message}
                              </span>
                            ) : null}
                          </td>
                          <td>
                            {renderPayloadActions(row, "request_payload")}
                          </td>
                          <td>
                            {renderPayloadActions(row, "response_payload")}
                          </td>
                          <td className="mdoc-audit-log__http-col">
                            {renderHttpStatusBadge(row.http_status_code)}
                          </td>
                          <td className="mdoc-audit-log__action-col">
                            {row.can_retry ? (
                              <button
                                type="button"
                                className="justo-report__btn justo-report__btn--outline justo-report__btn--sm mdoc-audit-log__retry-btn"
                                onClick={() => openRetryModal(row)}
                                disabled={Boolean(retryingId)}
                              >
                                <i className="fa fa-pencil" aria-hidden="true" />
                                <span>{strings.editAndRetry}</span>
                              </button>
                            ) : (
                              <span className="mdoc-audit-payload__na">—</span>
                            )}
                          </td>
                        </tr>
                        {isRowPayloadOpen(row._id) &&
                          renderExpandedPanels(row)}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {total > 0 && (
            <div className="justo-report__footer">
              <div className="justo-report__page-size">
                <label htmlFor="mdoc-audit-page-size">Rows per page</label>
                <select
                  id="mdoc-audit-page-size"
                  className="form-control justo-report__input"
                  value={itemsPerPage}
                  onChange={handlePageSizeChange}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <Pagination
                itemsPerPage={itemsPerPage}
                totalCount={total}
                listData={rows}
                onPageChange={handlePageChange}
                itemOffset={currentPage}
                setItemOffset={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>

      {retryTarget && (
        <div
          className="mdoc-audit-modal-backdrop"
          onClick={closeRetryModal}
          role="presentation"
        >
          <div
            className="mdoc-audit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mdoc-retry-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mdoc-audit-modal__head">
              <div>
                <h2 id="mdoc-retry-title" className="mdoc-audit-modal__title">
                  {strings.editAndRetry}
                </h2>
                <p className="mdoc-audit-modal__subtitle">
                  {resolveVisitorName(retryTarget)}
                  {retryTarget.phone_number
                    ? ` · ${retryTarget.phone_number}`
                    : ""}
                  {retryTarget.captured_by_name
                    ? ` · ${strings.capturedBy}: ${retryTarget.captured_by_name}`
                    : ""}
                </p>
              </div>
              <button
                type="button"
                className="mdoc-audit-modal__close"
                onClick={closeRetryModal}
                disabled={Boolean(retryingId)}
                aria-label={strings.cancel}
              >
                ×
              </button>
            </div>
            <div className="mdoc-audit-modal__body">
              <p className="mdoc-audit-modal__hint">
                {retryTarget.visitor_edit?.missing_fields?.length
                  ? strings.mdocMissingDetailsHint
                  : strings.retryMdocSyncConfirm}
              </p>
              <div className="mdoc-audit-modal__grid">
                <label className="mdoc-audit-modal__field">
                  <span className="mdoc-audit-modal__label">
                    {strings.firstName}
                  </span>
                  <input
                    className={`form-control justo-report__input${
                      fieldErrors.first_name
                        ? " mdoc-audit-modal__input--error"
                        : ""
                    }`}
                    type="text"
                    name="first_name"
                    value={retryForm.first_name}
                    onChange={handleRetryFieldChange}
                    disabled={Boolean(retryingId)}
                    autoComplete="off"
                  />
                  {fieldErrors.first_name ? (
                    <span className="mdoc-audit-modal__error">
                      {fieldErrors.first_name}
                    </span>
                  ) : null}
                </label>
                <label className="mdoc-audit-modal__field">
                  <span className="mdoc-audit-modal__label">
                    {strings.lastName}
                  </span>
                  <input
                    className="form-control justo-report__input"
                    type="text"
                    name="last_name"
                    value={retryForm.last_name}
                    onChange={handleRetryFieldChange}
                    disabled={Boolean(retryingId)}
                    autoComplete="off"
                  />
                </label>
                <label className="mdoc-audit-modal__field">
                  <span className="mdoc-audit-modal__label">
                    {strings.mobileNumber || strings.mobile}
                  </span>
                  <input
                    className={`form-control justo-report__input${
                      fieldErrors.mobile
                        ? " mdoc-audit-modal__input--error"
                        : ""
                    }`}
                    type="text"
                    name="mobile"
                    value={retryForm.mobile}
                    onChange={handleRetryFieldChange}
                    disabled={Boolean(retryingId)}
                    maxLength={10}
                    autoComplete="off"
                  />
                  {fieldErrors.mobile ? (
                    <span className="mdoc-audit-modal__error">
                      {fieldErrors.mobile}
                    </span>
                  ) : null}
                </label>
                <label className="mdoc-audit-modal__field">
                  <span className="mdoc-audit-modal__label">
                    {strings.email}
                  </span>
                  <input
                    className={`form-control justo-report__input${
                      fieldErrors.email
                        ? " mdoc-audit-modal__input--error"
                        : ""
                    }`}
                    type="email"
                    name="email"
                    value={retryForm.email}
                    onChange={handleRetryFieldChange}
                    disabled={Boolean(retryingId)}
                    autoComplete="off"
                  />
                  {fieldErrors.email ? (
                    <span className="mdoc-audit-modal__error">
                      {fieldErrors.email}
                    </span>
                  ) : null}
                </label>
                <label className="mdoc-audit-modal__field mdoc-audit-modal__field--full">
                  <span className="mdoc-audit-modal__label">{strings.dob}</span>
                  <input
                    className={`form-control justo-report__input${
                      fieldErrors.birth_date
                        ? " mdoc-audit-modal__input--error"
                        : ""
                    }`}
                    type="date"
                    name="birth_date"
                    value={retryForm.birth_date}
                    onChange={handleRetryFieldChange}
                    disabled={Boolean(retryingId)}
                    max={moment().subtract(15, "years").format("YYYY-MM-DD")}
                  />
                  {fieldErrors.birth_date ? (
                    <span className="mdoc-audit-modal__error">
                      {fieldErrors.birth_date}
                    </span>
                  ) : (
                    <span className="mdoc-audit-modal__help">
                      {strings.mdocDobOptionalHint}
                    </span>
                  )}
                </label>
              </div>
            </div>
            <div className="mdoc-audit-modal__foot">
              <button
                type="button"
                className="justo-report__btn justo-report__btn--ghost"
                onClick={closeRetryModal}
                disabled={Boolean(retryingId)}
              >
                {strings.cancel}
              </button>
              <button
                type="button"
                className="justo-report__btn justo-report__btn--solid"
                onClick={confirmRetry}
                disabled={Boolean(retryingId)}
              >
                <i className="fa fa-check" aria-hidden="true" />
                <span>
                  {retryingId ? strings.mdocRetrying : strings.saveAndRetry}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MdocAuditLog;
